import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import moment from "moment-timezone";
import { withSnackbar } from "notistack";
import { connect } from "react-redux";
import set from "lodash.set";
import { MuiPickersUtilsProvider, DateTimePicker } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import mixpanel from "mixpanel-browser";
import {
  createMuiTheme,
  makeStyles,
  ThemeProvider,
} from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";

import Header from "../../../components/Typography/Header";
import ResponseDialog from "../../../components/Dialogs/Response";
import Divider from "../../../components/Dividers/Dialog";
import {
  deleteIncidentMedia,
  getIncidentReport,
} from "../../../utils/appsyncOperations";
import { CircularProgress } from "@material-ui/core";
import LoadingButton from "../../Buttons/Loading";
import { getEditableValue, getValue } from "../../../utils/input";
import { updateIncidentReport } from "../../../utils/appsyncOperations";
import {
  subject as reportSubject,
  vehicle as reportVehicle,
  moment as momentConst,
  mixpanel as mixpanelConst,
} from "../../../constants";
import AlertDialog from "../Alert";
import MediaImage from "../../MediaImage";

const darkTheme = createMuiTheme({
  palette: {
    type: "dark",
  },
});

const styles = {
  pic: {
    height: "100%",
    width: "100%",
    objectFit: "contain",
    overFlow: "hidden",
  },
  loading: {
    display: "flex",
    flex: 1,
    margin: "16px 0px 32px 16px",
    justifyContent: "center",
    alignItems: "center",
  },
  menus: {
    width: 200,
  },
  picGrid: {
    height: 300,
  },
  picDesc: {
    marginBottom: 24,
  },
};

const useStyles = makeStyles({
  circularProgressContainer: {
    alignItems: "center",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
  },
  imageContainer: {
    minHeight: 80,
    position: "relative",
  },
  imageToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    left: 8,
    position: "absolute",
    right: 8,
    top: 11,
  },
  imageToolbarButton: {
    margin: 8,
  },
  imageToolbarButtonContainer: {
    position: "relative",
  },
  imageToolbarButtonsContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    borderBottomLeftRadius: 8,
  },
});

const IncidentReport = ({
  isOpen,
  onClose,
  reportId,
  enqueueSnackbar,
  userId,
  properties,
  onSaveSuccess = () => {},
}) => {
  const classes = useStyles();
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [incidentReport, setIncidentReport] = useState({});
  const [loading, setLoading] = useState(true);
  const [isEditable, setIsEditable] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteAlert, setDeleteAlert] = useState({
    onConfirm: () => null,
    open: false,
  });

  let timezone;
  if (incidentReport) {
    const property = properties[incidentReport.propertyId];
    if (property) {
      timezone = property.place.timezone;
    }
  }

  useEffect(() => {
    const init = async () => {
      try {
        const report = await getIncidentReport({ incidentId: reportId });
        setIncidentReport(report);
        setLoading(false);
      } catch (e) {
        enqueueSnackbar(e, { variant: "error" });
        onClose();
        console.error(e);
      }
    };
    init();
  }, []);

  const closeDeleteAlert = () =>
    setDeleteAlert((prevState) => ({ ...prevState, open: false }));

  const onSave = async () => {
    const { policeCase, subjects, vehicles } = incidentReport;
    if (policeCase && (!policeCase.agency || !policeCase.number)) {
      return enqueueSnackbar(
        "You must enter both a police case number and agency.",
        { variant: "error" }
      );
    }
    if (subjects) {
      for (const subject of subjects) {
        const { driverLicense, name } = subject;
        if (!name) {
          return enqueueSnackbar("Each subject must have a name.", {
            variant: "error",
          });
        }
        if (driverLicense && (!driverLicense.number || !driverLicense.state)) {
          return enqueueSnackbar(
            "You must enter both a license number and state for each subject.",
            { variant: "error" }
          );
        }
      }
    }
    if (vehicles) {
      for (const vehicle of vehicles) {
        const { vehicleLicense } = vehicle;
        if (
          vehicleLicense &&
          (!vehicleLicense.number || !vehicleLicense.state)
        ) {
          return enqueueSnackbar(
            "You must enter both a license number and state for each vehicle.",
            { variant: "error" }
          );
        }
      }
    }
    setSaveLoading(true);
    try {
      const input = {
        incidentId: incidentReport.incidentId,
        propertyId: incidentReport.propertyId,
        serviceProviderId: incidentReport.serviceProviderId,
        edited: {
          at: Date.now(),
          by: userId,
        },
        startTime: incidentReport.startTime,
        title: incidentReport.title,
        description: incidentReport.description,
        policeCase: incidentReport.policeCase,
        subjects: incidentReport.subjects,
        vehicles: incidentReport.vehicles,
        attachedMedia: incidentReport.attachedMedia,
        submitted: {
          at: incidentReport.submitted.at.valueOf(),
          by: incidentReport.submitted.by,
        },
      };
      const response = await updateIncidentReport(input);
      mixpanel.track(mixpanelConst.INCIDENT_REPORT_EDIT);
      onSaveSuccess(response);
      enqueueSnackbar("Incident report updated!", { variant: "success" });
      setSaveLoading(false);
      onClose();
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
      setSaveLoading(false);
    }
  };

  const mutationPending = saveLoading || deleteLoading;

  const renderSummary = () => {
    return (
      <>
        <Header label="Summary" />
        <Grid container spacing={2} justify="space-between">
          <Grid item xs={6}>
            <TextField
              label="Title"
              value={getEditableValue(incidentReport.title, isEditable)}
              onChange={(e) =>
                setIncidentReport({ ...incidentReport, title: e.target.value })
              }
              InputProps={{ readOnly: !isEditable }}
              variant="outlined"
              fullWidth
              disabled={isEditable && mutationPending}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Report ID"
              value={incidentReport.incidentId}
              InputProps={{ readOnly: true }}
              variant="outlined"
              fullWidth
              disabled={isEditable}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              multiline
              label="Submitted By"
              value={incidentReport.submitted.by}
              InputProps={{ readOnly: true }}
              variant="outlined"
              fullWidth
              disabled={isEditable}
            />
          </Grid>
          <Grid item xs={6}>
            <DateTimePicker
              ampm={false}
              format={momentConst.FORMAT}
              label="Submitted At"
              inputVariant="outlined"
              value={moment(incidentReport.submitted.at).tz(timezone)}
              onChange={(e) =>
                setIncidentReport({
                  ...incidentReport,
                  submitted: { ...incidentReport.submitted, at: e.valueOf() },
                })
              }
              fullWidth
              readOnly={!isEditable}
              disabled={isEditable && mutationPending}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              multiline
              label="Started At"
              value={moment(incidentReport.startTime)
                .tz(timezone)
                .format(momentConst.FORMAT)}
              InputProps={{ readOnly: true }}
              disabled={isEditable}
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              multiline
              label="Ended At"
              value={getValue(incidentReport.endTime, (time) =>
                moment(time).tz(timezone).format(momentConst.FORMAT)
              )}
              InputProps={{ readOnly: true }}
              disabled={isEditable}
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              label="Description"
              value={getEditableValue(incidentReport.description, isEditable)}
              onChange={(e) =>
                setIncidentReport({
                  ...incidentReport,
                  description: e.target.value,
                })
              }
              InputProps={{ readOnly: !isEditable }}
              variant="outlined"
              fullWidth
              disabled={isEditable && mutationPending}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  const renderPoliceCase = () => {
    return (
      <>
        <Header
          label="Police Case"
          action={
            isEditable
              ? incidentReport.policeCase
                ? {
                    onClick: () =>
                      setIncidentReport({
                        ...incidentReport,
                        policeCase: null,
                      }),
                    title: "Remove",
                  }
                : {
                    onClick: () =>
                      setIncidentReport({
                        ...incidentReport,
                        policeCase: { number: "", agency: "" },
                      }),
                    title: "Add",
                  }
              : null
          }
        />
        {incidentReport.policeCase ? (
          <Grid container spacing={2} justify="space-between">
            <Grid item xs={6}>
              <TextField
                label="Agency"
                value={getEditableValue(
                  incidentReport.policeCase.agency,
                  isEditable
                )}
                onChange={(e) =>
                  setIncidentReport({
                    ...incidentReport,
                    policeCase: {
                      ...incidentReport.policeCase,
                      agency: e.target.value,
                    },
                  })
                }
                InputProps={{ readOnly: !isEditable }}
                variant="outlined"
                fullWidth
                disabled={isEditable && mutationPending}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                multiline
                label="Case Number"
                value={getEditableValue(
                  incidentReport.policeCase.number,
                  isEditable
                )}
                onChange={(e) =>
                  setIncidentReport({
                    ...incidentReport,
                    policeCase: {
                      ...incidentReport.policeCase,
                      number: e.target.value,
                    },
                  })
                }
                InputProps={{ readOnly: !isEditable }}
                variant="outlined"
                fullWidth
                disabled={isEditable && mutationPending}
              />
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">No police case added.</Typography>
        )}
      </>
    );
  };

  const setValue = (index, path, value, type) => {
    const temp = [...incidentReport[type]];
    set(temp[index], path, value);
    setIncidentReport({
      ...incidentReport,
      [type]: temp,
    });
  };

  const setVehicleValue = (index, path, value) => {
    setValue(index, path, value, "vehicles");
  };

  const setSubjectValue = (index, path, value) => {
    setValue(index, path, value, "subjects");
  };

  const setMediaValue = (index, path, value) => {
    setValue(index, path, value, "attachedMedia");
  };

  const renderMedia = () => {
    return (
      <>
        <Header label="Media" />
        {Array.isArray(incidentReport.attachedMedia) &&
        incidentReport.attachedMedia.length > 0 ? (
          incidentReport.attachedMedia.map((media, index) => {
            return (
              <Grid
                container
                justify="space-between"
                key={media.mediaId}
                spacing={2}
              >
                <Grid className={classes.imageContainer} item xs={12}>
                  <MediaImage media={media} style={styles.pic} />
                  <div className={classes.imageToolbar}>
                    <div className={classes.imageToolbarButtonsContainer}>
                      <div className={classes.imageToolbarButtonContainer}>
                        <ThemeProvider theme={darkTheme}>
                          <IconButton
                            className={classes.imageToolbarButton}
                            disabled={isEditable || mutationPending}
                            onClick={() => {
                              setDeleteIndex(index);
                              setDeleteAlert({
                                onConfirm: async () => {
                                  setDeleteLoading(true);
                                  try {
                                    const updatedIncidentReport = await deleteIncidentMedia(
                                      { mediaId: media.mediaId, reportId }
                                    );
                                    setIncidentReport(updatedIncidentReport);
                                    onSaveSuccess(updatedIncidentReport);
                                    enqueueSnackbar("Media deleted!", {
                                      variant: "success",
                                    });
                                  } catch (error) {
                                    enqueueSnackbar(error, {
                                      variant: "error",
                                    });
                                  }
                                  setDeleteLoading(false);
                                },
                                open: true,
                              });
                            }}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ThemeProvider>
                        {deleteLoading && index === deleteIndex && (
                          <div className={classes.circularProgressContainer}>
                            <CircularProgress size={24} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Grid>
                {Boolean(media.createdAt) && (
                  <Grid item xs={12}>
                    <TextField
                      label="Created At"
                      value={moment(media.createdAt)
                        .tz(timezone)
                        .format(momentConst.format)}
                      disabled={isEditable}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    style={styles.picDesc}
                    label="Description"
                    value={getEditableValue(media.description, isEditable)}
                    onChange={(e) =>
                      setMediaValue(index, "description", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={saveLoading}
                  />
                </Grid>
              </Grid>
            );
          })
        ) : (
          <Typography variant="body1">No media added.</Typography>
        )}
      </>
    );
  };

  const renderVehicles = () => {
    return (
      <>
        <Header label="Vehicles" />
        {incidentReport.vehicles ? (
          incidentReport.vehicles.map((vehicle, index) => {
            return (
              <Grid container spacing={2} justify="space-between">
                <Grid item xs={6}>
                  <TextField
                    label="Registered Owner"
                    value={getEditableValue(
                      vehicle.registeredOwner,
                      isEditable
                    )}
                    onChange={(e) =>
                      setVehicleValue(index, "registeredOwner", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    select
                    label="Type"
                    value={getEditableValue(vehicle.type, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "type", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportVehicle.type).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    select
                    label="Status"
                    value={getEditableValue(vehicle.status, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "status", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportVehicle.status).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    multiline
                    label="Description"
                    value={getEditableValue(vehicle.description, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "description", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Make"
                    value={getEditableValue(vehicle.make, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "make", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Model"
                    value={getEditableValue(vehicle.model, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "model", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    type="number"
                    label="Year"
                    value={getEditableValue(vehicle.year, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "year", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Color"
                    value={getEditableValue(vehicle.color, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "color", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="License Plate Number"
                    value={getEditableValue(
                      vehicle.vehicleLicense == null
                        ? ""
                        : vehicle.vehicleLicense.number,
                      isEditable
                    )}
                    onChange={(e) =>
                      setVehicleValue(
                        index,
                        "vehicleLicense.number",
                        e.target.value
                      )
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="License Plate State"
                    value={getEditableValue(
                      vehicle.vehicleLicense == null
                        ? ""
                        : vehicle.vehicleLicense.state,
                      isEditable
                    )}
                    onChange={(e) =>
                      setVehicleValue(
                        index,
                        "vehicleLicense.state",
                        e.target.value
                      )
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="VIN"
                    value={getEditableValue(vehicle.vin, isEditable)}
                    onChange={(e) =>
                      setVehicleValue(index, "vin", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
              </Grid>
            );
          })
        ) : (
          <Typography variant="body1">No vehicles added.</Typography>
        )}
      </>
    );
  };

  const renderSubjects = () => {
    return (
      <>
        <Header label="Subjects" />
        {incidentReport.subjects ? (
          incidentReport.subjects.map((subject, index) => {
            return (
              <Grid container spacing={2} justify="space-between">
                <Grid item xs={6}>
                  <TextField
                    label="Name"
                    value={getEditableValue(subject.name, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "name", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    required={isEditable}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Type"
                    value={getEditableValue(subject.type, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "type", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportSubject.type).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    multiline
                    label="Description"
                    value={getEditableValue(subject.description, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "description", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    select
                    label="Sex"
                    value={getEditableValue(subject.sex, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "sex", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportSubject.sex).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    label="Address"
                    value={getEditableValue(subject.address, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "address", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select
                    label="Ethnicity"
                    value={getEditableValue(subject.ethnicity, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "ethnicity", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportSubject.ethnicity).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select
                    label="Race"
                    value={getEditableValue(subject.race, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "race", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportSubject.race).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Phone Number"
                    value={getEditableValue(subject.phoneNumber, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "phoneNumber", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Height"
                    value={getEditableValue(subject.height, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "height", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Weight"
                    value={getEditableValue(subject.weight, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "weight", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Age"
                    value={getEditableValue(subject.age, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "age", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select
                    label="Hair Color"
                    value={getEditableValue(subject.hairColor, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "hairColor", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportSubject.hairColor).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    select
                    label="Eye Color"
                    value={getEditableValue(subject.eyeColor, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "eyeColor", e.target.value)
                    }
                    SelectProps={{
                      MenuProps: {
                        style: styles.menus,
                      },
                    }}
                    InputProps={{ readOnly: !isEditable }}
                    fullWidth
                    variant="outlined"
                    disabled={isEditable && mutationPending}
                  >
                    {Object.values(reportSubject.eyeColor).map((variant) => (
                      <MenuItem key={variant.value} value={variant.value}>
                        {variant.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Date of Birth"
                    value={getEditableValue(subject.dateOfBirth, isEditable)}
                    onChange={(e) =>
                      setSubjectValue(index, "dateOfBirth", e.target.value)
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Driver License Number"
                    value={getEditableValue(
                      subject.driverLicense == null
                        ? ""
                        : subject.driverLicense.number,
                      isEditable
                    )}
                    onChange={(e) =>
                      setSubjectValue(
                        index,
                        "driverLicense.number",
                        e.target.value
                      )
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Driver License State"
                    value={getEditableValue(
                      subject.driverLicense == null
                        ? ""
                        : subject.driverLicense.state,
                      isEditable
                    )}
                    onChange={(e) =>
                      setSubjectValue(
                        index,
                        "driverLicense.state",
                        e.target.value
                      )
                    }
                    InputProps={{ readOnly: !isEditable }}
                    variant="outlined"
                    fullWidth
                    disabled={isEditable && mutationPending}
                  />
                </Grid>
              </Grid>
            );
          })
        ) : (
          <Typography variant="body1">No subjects added.</Typography>
        )}
      </>
    );
  };

  return (
    <>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <Dialog
          open={isOpen}
          onClose={onClose}
          fullWidth
          maxWidth="md"
          scroll="paper"
        >
          <DialogTitle>{`Incident Report${
            isEditable ? " \u2022 Editable" : ""
          }`}</DialogTitle>
          {loading ? (
            <div style={styles.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <DialogContent>
                {renderSummary()}
                <Divider />
                {renderPoliceCase()}
                <Divider />
                {renderSubjects()}
                <Divider />
                {renderVehicles()}
                <Divider />
                {renderMedia()}
              </DialogContent>
              <DialogActions>
                {incidentReport.connectedInstance && (
                  <Button
                    onClick={() => setShowResponseDialog(true)}
                    color="primary"
                  >
                    Response
                  </Button>
                )}
                {isEditable ? (
                  <LoadingButton
                    isLoading={saveLoading}
                    color="primary"
                    onClick={onSave}
                  >
                    Save
                  </LoadingButton>
                ) : (
                  <Button
                    disabled={deleteLoading}
                    onClick={() => setIsEditable(true)}
                    color="primary"
                  >
                    Edit
                  </Button>
                )}
                <Button onClick={onClose} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </MuiPickersUtilsProvider>
      {incidentReport.connectedInstance && (
        <ResponseDialog
          isOpen={showResponseDialog}
          onClose={() => setShowResponseDialog(false)}
          instanceId={incidentReport.connectedInstance}
        />
      )}
      <AlertDialog
        content="Are you sure you want to delete this image?"
        isOpen={deleteAlert.open}
        onClose={closeDeleteAlert}
        onNo={closeDeleteAlert}
        onYes={() => {
          closeDeleteAlert();
          deleteAlert.onConfirm();
        }}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  userId: state.auth.user.username,
  properties: state.properties.byId,
});

export default withSnackbar(connect(mapStateToProps)(IncidentReport));
